import { Europafacts } from "@/services/api";
import type {
  BookmarkResponse,
  BookmarkSet,
  BookmarkSetBookmark,
  BookmarkSetResponse,
} from "@/types/europafacts";
import type { BookmarksStore } from "@/types/stores/bookmark";
import { logger } from "@/utils/debug";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

const initialState = {
  bookmarks: [],
  bookmarkSets: [],
  loading: false,
  error: null,
};

const ACCESS_TOKEN_KEY = "europafacts_access_token";

const getAccessToken = (): string => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (!token) {
    throw new Error("Authentication required - no access token found");
  }
  return token;
};

const handleApiError = (error: any, res?: Response): Error => {
  if (res && (res.status === 401 || res.status === 403)) {
    Europafacts.logout();
    return new Error("Authentication failed - please log in again");
  }
  if (error instanceof TypeError && error.message.includes("Failed to fetch")) {
    return new Error(
      "Network error - please check your connection and try again"
    );
  }
  return error instanceof Error ? error : new Error(String(error));
};

async function fetchWithAuth(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getAccessToken();
  const res = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    },
  });
  return res;
}

export const useBookmarksStore = create<BookmarksStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        fetchBookmarks: async () => {
          try {
            set(
              { loading: true, error: null },
              false,
              "bookmarks/fetchBookmarks"
            );

            const res = await fetchWithAuth(
              `/europafacts/db-users/bookmark/my/list/api/`,
              { method: "GET" }
            );

            if (!res.ok) {
              const errorText = await res.text();
              const error = new Error(
                `API request failed: ${res.status} - ${errorText}`
              );
              const handledError = handleApiError(error, res);
              set(
                { error: handledError.message, loading: false },
                false,
                "bookmarks/fetchBookmarks"
              );
              logger.error("Failed to fetch bookmarks:", error);
              return;
            }

            const data: BookmarkResponse = await res.json();
            set(
              { bookmarks: data.results, loading: false },
              false,
              "bookmarks/fetchBookmarks"
            );
          } catch (error) {
            const errorMessage = handleApiError(error);
            set(
              { error: errorMessage.message, loading: false },
              false,
              "bookmarks/fetchBookmarks"
            );
            logger.error("Failed to fetch bookmarks:", error);
          }
        },

        fetchBookmarkSets: async () => {
          try {
            set(
              { loading: true, error: null },
              false,
              "bookmarks/fetchBookmarkSets"
            );

            const res = await fetchWithAuth(
              `/europafacts/db-users/bookmark-set/my/list/api/`,
              { method: "GET" }
            );

            if (!res.ok) {
              const errorText = await res.text();
              const error = new Error(
                `API request failed: ${res.status} - ${errorText}`
              );
              const handledError = handleApiError(error, res);
              set(
                { error: handledError.message, loading: false },
                false,
                "bookmarks/fetchBookmarkSets"
              );
              logger.error("Failed to fetch bookmark sets:", error);
              return;
            }

            const data: BookmarkSetResponse = await res.json();
            set(
              { bookmarkSets: data.results, loading: false },
              false,
              "bookmarks/fetchBookmarkSets"
            );
          } catch (error) {
            const errorMessage = handleApiError(error);
            set(
              { error: errorMessage.message, loading: false },
              false,
              "bookmarks/fetchBookmarkSets"
            );
            logger.error("Failed to fetch bookmark sets:", error);
          }
        },

        toggleBookmark: async (datasetId: string) => {
          try {
            const res = await fetchWithAuth(
              `/europafacts/db-users/bookmark/my/${datasetId}/toggle/api/`,
              { method: "GET" }
            );

            if (!res.ok) {
              const errorText = await res.text();
              const error = new Error(
                `API request failed: ${res.status} - ${errorText}`
              );
              handleApiError(error, res);
              logger.error("Failed to toggle bookmark:", error);
              return;
            }

            await get().fetchBookmarks();
          } catch (error) {
            handleApiError(error);
            logger.error("Failed to toggle bookmark:", error);
          }
        },

        removeBookmark: async (bookmarkId: string) => {
          try {
            const res = await fetchWithAuth(
              `/europafacts/db-users/bookmark/my/${bookmarkId}/api/`,
              { method: "DELETE" }
            );

            if (!res.ok) {
              const errorText = await res.text();
              const error = new Error(
                `API request failed: ${res.status} - ${errorText}`
              );
              handleApiError(error, res);
              logger.error("Failed to remove bookmark:", error);
              return;
            }

            await get().fetchBookmarks();
          } catch (error) {
            handleApiError(error);
            logger.error("Failed to remove bookmark:", error);
          }
        },

        isBookmarked: (datasetId: string) => {
          const inMyBookmarks = get().bookmarks.some(
            (bookmark) => bookmark.db_table.db_table_id === datasetId
          );

          const inBookmarkSet = get().bookmarkSets.some((bookmarkSet) =>
            bookmarkSet.bookmark_set_bookmark_list?.some(
              (bsb: BookmarkSetBookmark) => bsb.db_table_id === datasetId
            )
          );

          return inMyBookmarks || inBookmarkSet;
        },

        refreshBookmarks: async () => {
          await Promise.all([
            get().fetchBookmarks(),
            get().fetchBookmarkSets(),
          ]);
        },

        getBookmarkFromTableId: (tableId: string) => {
          return get().bookmarks.find(
            (bookmark) => bookmark.db_table.db_table_id === tableId
          );
        },

        saveToMyBookmarks: async (datasetId: string) => {
          try {
            await Europafacts.fetch(
              `/db-users/bookmark/my/${datasetId}/set/api/`,
              {
                method: "GET",
              }
            );
            await get().refreshBookmarks();
          } catch (error) {
            logger.error("Failed to save to my bookmarks:", error);
            throw error;
          }
        },

        removeFromMyBookmarks: async (datasetId: string) => {
          try {
            await Europafacts.fetch(`/db-users/bookmark/my/${datasetId}/api/`, {
              method: "DELETE",
            });
            await get().refreshBookmarks();
          } catch (error) {
            logger.error("Failed to remove from my bookmarks:", error);
            throw error;
          }
        },

        saveToBookmarkSet: async (bookmarkSetId: string, datasetId: string) => {
          try {
            await Europafacts.fetch(
              `/db-users/bookmark-set-bookmark/insert/api/`,
              {
                method: "POST",
                body: JSON.stringify({
                  bookmark_set_id: bookmarkSetId,
                  db_table_id: datasetId,
                  description: null,
                }),
              }
            );
            await get().refreshBookmarks();
          } catch (error) {
            logger.error("Failed to save to bookmark set:", error);
            throw error;
          }
        },

        removeFromBookmarkSet: async (
          bookmarkSetId: string,
          datasetId: string
        ) => {
          try {
            const bookmarkSet = get().bookmarkSets.find(
              (bs: BookmarkSet) => bs.bookmark_set_id === bookmarkSetId
            );
            const bookmarkSetBookmark =
              bookmarkSet?.bookmark_set_bookmark_list?.find(
                (bsb: BookmarkSetBookmark) => bsb.db_table_id === datasetId
              );

            if (!bookmarkSetBookmark) {
              logger.error("Bookmark not found in bookmark set");
              throw new Error("Bookmark not found in bookmark set");
            }

            await Europafacts.fetch(
              `/db-users/bookmark-set-bookmark/${bookmarkSetBookmark.bookmark_set_bookmark_id}/api`,
              {
                method: "DELETE",
              }
            );
            await get().refreshBookmarks();
          } catch (error) {
            logger.error("Failed to remove from bookmark set:", error);
            throw error;
          }
        },

        createBookmarkSet: async (
          bookmarkSetName: string,
          _userId: string,
          datasetId: string
        ) => {
          try {
            const encodedName = encodeURIComponent(bookmarkSetName.trim());
            const result = await Europafacts.fetch<BookmarkSet>(
              `/db-users/bookmark-set/create-new/api/?bookmark_set_name=${encodedName}`,
              {
                method: "GET",
              }
            );

            const bookmarkSetId = result.data.bookmark_set_id;
            await get().saveToBookmarkSet(bookmarkSetId, datasetId);
          } catch (error) {
            logger.error("Failed to create new bookmark set:", error);
            throw error;
          }
        },
      }),
      {
        name: "bookmarks-store",
        partialize: (state) => ({
          bookmarks: state.bookmarks,
          bookmarkSets: state.bookmarkSets,
        }),
      }
    ),
    {
      name: "bookmarks-store",
    }
  )
);

export const useBookmarks = () => useBookmarksStore((state) => state.bookmarks);
export const useBookmarkSets = () =>
  useBookmarksStore((state) => state.bookmarkSets);
export const useBookmarksLoading = () =>
  useBookmarksStore((state) => state.loading);
export const useBookmarksError = () =>
  useBookmarksStore((state) => state.error);
export const useFetchBookmarks = () =>
  useBookmarksStore((state) => state.fetchBookmarks);
export const useToggleBookmark = () =>
  useBookmarksStore((state) => state.toggleBookmark);
export const useRemoveBookmark = () =>
  useBookmarksStore((state) => state.removeBookmark);
export const useRefreshBookmarks = () =>
  useBookmarksStore((state) => state.refreshBookmarks);
export const useSaveToMyBookmarks = () =>
  useBookmarksStore((state) => state.saveToMyBookmarks);
export const useRemoveFromMyBookmarks = () =>
  useBookmarksStore((state) => state.removeFromMyBookmarks);
export const useSaveToBookmarkSet = () =>
  useBookmarksStore((state) => state.saveToBookmarkSet);
export const useRemoveFromBookmarkSet = () =>
  useBookmarksStore((state) => state.removeFromBookmarkSet);
export const useCreateBookmarkSet = () =>
  useBookmarksStore((state) => state.createBookmarkSet);
export const useBookmarksContext = () => useBookmarksStore();
